import { useSetContainerValues } from '@/components/container/context/context';
import { SheetAlertContentLight, SheetAlertLight } from '@/components/sheet-alert/light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { formatDate, formatPhone } from '@/lib';
import { ChevronRight, Loader } from '@nosinovacao/fe-icons';
import { ActionCard, Captcha, cn, If, Toggle, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link, Outlet } from '@tanstack/react-router';
import { type SecurityProps } from '../-model/props';

export function SecurityLight({
  canChangeTwoFactor,
  twofactorActive,
  email,
  phone,
  isLoading,
  captcha,
  changeTwoFactor,
  changeTwoFactorLoading,
  mfaActive,
  mfaDevice,
  mfaEnabledAt,
  mfaRecoveryCodesRemaining,
  mfaAlertOpen,
  setMfaAlertOpen,
  removeMfa,
  removeMfaLoading,
  changeMfa,
  changeMfaLoading,
}: Readonly<SecurityProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Security.Title'),
  });
  return (
    <>
      <main className="grid gap-6 desktop-md:grid-cols-[1fr_minmax(16rem,max-content)] desktop-lg:grid-cols-[1fr_minmax(21.25rem,max-content)]">
        <div className="grid h-fit gap-4">
          <SkeletonLight loading={isLoading} className="h-[309px] rounded-lg tablet:h-[275px]">
            <div className="grid gap-4 rounded-lg border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
              <div className="grid gap-3">
                <div className="grid grid-cols-[1fr_auto] gap-x-4">
                  <h2
                    data-tid="title-two-factor"
                    className="font-bold text-nos-neutral-900 font-text-p1"
                  >
                    {t('Security.TwoFactorAuthCard.Title')}
                  </h2>
                  <div>
                    <If condition={canChangeTwoFactor}>
                      <If condition={changeTwoFactorLoading}>
                        <Loader width={24} height={24} />
                      </If>
                      <If condition={!changeTwoFactorLoading}>
                        <Toggle
                          data-tid="toggle-two-factor"
                          skin="light"
                          checked={twofactorActive}
                          disabled={!canChangeTwoFactor}
                          onChange={changeTwoFactor}
                        />
                      </If>
                    </If>
                  </div>
                  <span
                    className={cn(
                      'font-text-p2',
                      twofactorActive ? 'text-nos-green' : 'text-nos-red',
                    )}
                  >
                    <If condition={twofactorActive}>{t('Security.TwoFactorAuthCard.Active')}</If>
                    <If condition={!twofactorActive}>{t('Security.TwoFactorAuthCard.Inactive')}</If>
                  </span>
                </div>
                {/* <If condition={!canChangeTwoFactor}>
                <Tip
                  data-tid="tip-two-factor"
                  skin="light"
                  title={t('Security.TwoFactorAuthCard.Tip.Title')}
                  description={t('Security.TwoFactorAuthCard.Tip.Description')}
                  type="info"
                />
              </If> */}
                <span className="text-nos-neutral-500 font-text-p2">
                  {t('Security.TwoFactorAuthCard.Description')}
                </span>
              </div>
              <div className="h-px w-full bg-nos-neutral-100" />
              <div className="grid gap-3">
                <span className="font-medium text-neutral-900 font-text-p2">
                  {t('Security.TwoFactorAuthCard.Contacts.Title')}
                </span>
                <TwoFactorContact label="Phone" value={phone ? formatPhone(phone) : undefined} />
                <TwoFactorContact label="Email" value={email} />
                <If condition={captcha.challenge}>
                  <Captcha onChange={captcha.setToken} />
                </If>
              </div>
            </div>
          </SkeletonLight>
          <SkeletonLight loading={isLoading} className="h-[180px] rounded-lg">
            <div className="grid gap-4 rounded-lg border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
              <div className="grid gap-3">
                <div className="grid grid-cols-[1fr_auto] gap-x-4">
                  <h2
                    data-tid="title-authenticator-app"
                    className="font-bold text-nos-neutral-900 font-text-p1"
                  >
                    {t('Security.AuthenticatorAppCard.Title')}
                  </h2>
                  <div>
                    <If condition={changeMfaLoading}>
                      <Loader width={24} height={24} />
                    </If>
                    <If condition={!changeMfaLoading}>
                      <Toggle
                        data-tid="toggle-authenticator-app"
                        skin="light"
                        checked={mfaActive}
                        onChange={changeMfa}
                      />
                    </If>
                  </div>
                  <span
                    className={cn('font-text-p2', mfaActive ? 'text-nos-green' : 'text-nos-red')}
                  >
                    <If condition={mfaActive}>{t('Security.AuthenticatorAppCard.Active')}</If>
                    <If condition={!mfaActive}>{t('Security.AuthenticatorAppCard.Inactive')}</If>
                  </span>
                </div>
                <span className="text-nos-neutral-500 font-text-p2">
                  {t('Security.AuthenticatorAppCard.Description')}
                </span>
              </div>
              <If condition={mfaActive}>
                <div className="h-px w-full bg-nos-neutral-100" />
                <div className="grid gap-3">
                  <span className="font-medium text-neutral-900 font-text-p2">
                    {t('Security.AuthenticatorAppCard.Details.Title')}
                  </span>
                  <AuthenticatorAppDetail label="Device" tid="device" value={mfaDevice} />
                  <AuthenticatorAppDetail
                    label="EnabledAt"
                    tid="enabled-at"
                    value={mfaEnabledAt ? formatDate(mfaEnabledAt) : undefined}
                  />
                  <AuthenticatorAppDetail
                    label="RecoveryCodesRemaining"
                    tid="recovery-codes-remaining"
                    value={mfaRecoveryCodesRemaining?.toString()}
                  />
                </div>
              </If>
            </div>
          </SkeletonLight>
        </div>
        <div className="grid h-fit gap-2">
          <h2 className="font-light text-nos-neutral-900 font-text-p1">
            {t('Security.OtherSecurityAlternatives.Title')}
          </h2>
          <ActionCard
            data-tid="card-account-activity"
            skin="light"
            variant="quick-link"
            preTitle={t('Security.OtherSecurityAlternatives.View')}
            subTitle={<></>}
            title={t('Security.OtherSecurityAlternatives.AccountActivity')}
            icon={<ChevronRight width={20} height={20} />}
            className="w-full bg-nos-neutral-0 shadow-1"
            asChild
          >
            <Link to="/security/account-activity" />
          </ActionCard>
          <ActionCard
            data-tid="card-active-sessions"
            skin="light"
            variant="quick-link"
            preTitle={t('Security.OtherSecurityAlternatives.Logout')}
            subTitle={<></>}
            title={t('Security.OtherSecurityAlternatives.ActiveSessions')}
            icon={<ChevronRight width={20} height={20} />}
            className="w-full bg-nos-neutral-0 shadow-1"
            asChild
          >
            <Link to="/security/active-sessions" />
          </ActionCard>
        </div>
      </main>
      <SheetAlertLight
        data-tid="alert-remove-authenticator-app"
        open={mfaAlertOpen}
        onOpenChange={(o) => {
          if (o || !removeMfaLoading) setMfaAlertOpen(o);
        }}
      >
        <SheetAlertContentLight
          type="destructive"
          title={t('Security.AuthenticatorAppCard.Alert.Title')}
          description={t('Security.AuthenticatorAppCard.Alert.Description')}
          cancel={{
            children: t('Security.AuthenticatorAppCard.Alert.Cancel'),
            disabled: removeMfaLoading,
            onClick: () => setMfaAlertOpen(false),
          }}
          confirm={{
            children: removeMfaLoading ? (
              <Loader height={24} width={24} />
            ) : (
              t('Security.AuthenticatorAppCard.Alert.Confirm')
            ),
            disabled: removeMfaLoading,
            onClick: removeMfa,
          }}
        />
      </SheetAlertLight>
      <Outlet />
    </>
  );
}

function TwoFactorContact({
  label,
  value,
}: {
  value: string | undefined | null;
  label: 'Phone' | 'Email';
}) {
  const { t } = useI18n();
  if (!value) return null;
  return (
    <div className="grid gap-1">
      <span className="text-nos-neutral-500 font-text-p3">
        {t(`Security.TwoFactorAuthCard.Contacts.${label}`)}
      </span>
      <span
        data-tid={`text-two-factor-${label.toLowerCase()}`}
        className="text-nos-neutral-900 font-text-p2"
      >
        {value}
      </span>
    </div>
  );
}

function AuthenticatorAppDetail({
  label,
  tid,
  value,
}: {
  value: string | undefined | null;
  label: 'Device' | 'EnabledAt' | 'RecoveryCodesRemaining';
  tid: string;
}) {
  const { t } = useI18n();
  if (!value) return null;
  return (
    <div className="grid gap-1">
      <span className="text-nos-neutral-500 font-text-p3">
        {t(`Security.AuthenticatorAppCard.Details.${label}`)}
      </span>
      <span
        data-tid={`text-authenticator-app-${tid}`}
        className="text-nos-neutral-900 font-text-p2"
      >
        {value}
      </span>
    </div>
  );
}
